import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { companyConfigService, aiCallService, knowledgeService } from '../services';
import { supabase } from '../supabase';
import type { CompanyConfig } from '../repositories/CompanyConfigRepository';
import type { AICall, AICallStats } from '../repositories/AICallRepository';

/**
 * Query keys for AI voice agents data
 */
export const aiVoiceAgentKeys = {
  all: ['aiVoiceAgents'] as const,
  companies: () => [...aiVoiceAgentKeys.all, 'companies'] as const,
  activeCompanies: () => [...aiVoiceAgentKeys.companies(), 'active'] as const,
  activeCompaniesCount: () => [...aiVoiceAgentKeys.companies(), 'activeCount'] as const,
  calls: () => [...aiVoiceAgentKeys.all, 'calls'] as const,
  callStats: () => [...aiVoiceAgentKeys.calls(), 'stats'] as const,
  recentCalls: (limit?: number) => [...aiVoiceAgentKeys.calls(), 'recent', limit] as const,
  knowledgeDocumentsCount: () => [...aiVoiceAgentKeys.all, 'knowledgeDocuments', 'count'] as const,
  agentRulesCount: () => [...aiVoiceAgentKeys.all, 'agentRules', 'count'] as const,
};

/**
 * Hook to get knowledge base documents count
 */
export function useKnowledgeDocumentsCount() {
  return useQuery({
    queryKey: aiVoiceAgentKeys.knowledgeDocumentsCount(),
    queryFn: async () => {
      const { count, error } = await supabase
        .from('knowledge_base_documents')
        .select('id', { count: 'exact', head: true });
      if (error) throw error;
      return count || 0;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to get AI agent rules count
 */
export function useAgentRulesCount() {
  return useQuery({
    queryKey: aiVoiceAgentKeys.agentRulesCount(),
    queryFn: async () => {
      const { count, error } = await supabase
        .from('ai_agent_rules')
        .select('id', { count: 'exact', head: true });
      if (error) throw error;
      return count || 0;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to get active companies count
 */
export function useActiveCompaniesCount() {
  return useQuery({
    queryKey: aiVoiceAgentKeys.activeCompaniesCount(),
    queryFn: () => companyConfigService.getActiveCompaniesCount(),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Hook to get all active companies
 */
export function useActiveCompanies() {
  return useQuery({
    queryKey: aiVoiceAgentKeys.activeCompanies(),
    queryFn: () => companyConfigService.getActiveCompanies(),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Hook to get all companies
 */
export function useAllCompanies() {
  return useQuery({
    queryKey: aiVoiceAgentKeys.companies(),
    queryFn: () => companyConfigService.getAllCompanies(),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Hook to get AI call statistics
 */
export function useAICallStats() {
  return useQuery({
    queryKey: aiVoiceAgentKeys.callStats(),
    queryFn: () => aiCallService.getCallStats(),
    staleTime: 30 * 1000, // 30 seconds - more frequent updates for stats
  });
}

/**
 * Hook to get recent AI calls with company information
 */
export function useRecentAICalls(limit: number = 10) {
  return useQuery({
    queryKey: aiVoiceAgentKeys.recentCalls(limit),
    queryFn: () => aiCallService.getRecentCallsWithCompany(limit),
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook to get all AI calls
 */
export function useAllAICalls() {
  return useQuery({
    queryKey: aiVoiceAgentKeys.calls(),
    queryFn: () => aiCallService.getAllCalls(),
    staleTime: 1 * 60 * 1000, // 1 minute
  });
}

/**
 * Hook to create a company
 */
export function useCreateCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<CompanyConfig>) => companyConfigService.createCompany(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiVoiceAgentKeys.companies() });
    },
  });
}

/**
 * Hook to update a company
 */
export function useUpdateCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CompanyConfig> }) =>
      companyConfigService.updateCompany(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiVoiceAgentKeys.companies() });
    },
  });
}

/**
 * Hook to update company active status
 */
export function useUpdateCompanyStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      companyConfigService.updateActiveStatus(id, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiVoiceAgentKeys.companies() });
      queryClient.invalidateQueries({ queryKey: aiVoiceAgentKeys.activeCompaniesCount() });
    },
  });
}

/**
 * Hook to delete a company
 */
export function useDeleteCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => companyConfigService.deleteCompany(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: aiVoiceAgentKeys.companies() });
    },
  });
}

/**
 * Combined hook for AI Voice Agents Dashboard
 * Provides all data needed for the overview page in a single hook
 */
export function useAIVoiceAgentsDashboard() {
  const activeCompaniesCount = useActiveCompaniesCount();
  const callStats = useAICallStats();
  const recentCalls = useRecentAICalls(10);
  const documentsCount = useKnowledgeDocumentsCount();
  const rulesCount = useAgentRulesCount();

  return {
    activeCompaniesCount: activeCompaniesCount.data ?? 0,
    totalDocuments: documentsCount.data ?? 0,
    totalRules: rulesCount.data ?? 0,
    stats: callStats.data ?? {
      totalCalls: 0,
      activeCalls: 0,
      completedCalls: 0,
      transferredCalls: 0,
      avgCallDuration: 0,
      transferRate: 0,
    },
    recentCalls: recentCalls.data ?? [],
    isLoading: activeCompaniesCount.isLoading || callStats.isLoading || recentCalls.isLoading || 
               documentsCount.isLoading || rulesCount.isLoading,
    error: activeCompaniesCount.error || callStats.error || recentCalls.error || 
           documentsCount.error || rulesCount.error,
  };
}
